import { AutomatorBuilderPublishSetting } from '../_base';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';

// Import global utils
import { openWindow } from '@automator/shared/utils/open-window';
import { getFormattedDuration } from '@automator/shared/utils/get-formatted-duration';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-publish-setting-throttle' )
export class AutomatorBuilderPublishSettingThrottle extends AutomatorBuilderPublishSetting {
	// Custom: Get the recipe type
	recipeType = UncannyAutomator._recipe.recipe_type;

	// Unique identifier for this setting.
	settingID = 'recipe_throttle';

	// Icon representing this setting in the UI.
	icon = 'gauge-max';

	// Define the helper text for the setting.
	helper = escapeHTML( __( 'Runs this recipe only once per specified period, even if triggered multiple times.', 'uncanny-automator' ) );

	/**
	 * Defines the fields available within this setting. Each object in this array represents a field that can be configured by the user, including input type, label, and other attributes.
	 */
	fieldsDirectory = [
		{
			option_code: 'ENABLED', // Unique code for the "Enable" toggle.
			input_type: 'checkbox', // Specifies the input type as integer.
			is_toggle: true, // Specifies that this field is a toggle.
			label: escapeHTML( __( 'Enable throttling', 'uncanny-automator' ) ), // Label for the "Enable" toggle.
			description: escapeHTML( __( 'When enabled, this recipe will only run once within the defined time period, even if triggered multiple times. For example, if set to "1 day," the recipe will run only once per day, regardless of how many times it is triggered.', 'uncanny-automator' ) ), // Field description shown to the user.
		},

		// Add "Throttle scope" only to "Logged-in" recipes
		...( this.recipeType === 'user' ? [ {
			option_code: 'PER_RECIPE_OR_USER',
			input_type: 'select',
			label: escapeHTML( __( 'Throttle scope', 'uncanny-automator' ) ),
			description: escapeHTML( __( 'Define whether the limit applies globally or per user.', 'uncanny-automator' ) ),
			options_show_id: false,
			options: [
				{
					value: 'recipe',
					text: escapeHTML( __( 'Per recipe — Shared limit for all users', 'uncanny-automator' ) ),
				},
				{
					value: 'user',
					text: escapeHTML( __( 'Per user — Each user has their own independent limit', 'uncanny-automator' ) ),
				}
			],
			required: true,
			dynamic_visibility: {
				default_state: 'hidden',
				visibility_rules: [
					{
						operator: 'AND',
						rule_conditions: [
							{
								option_code: 'ENABLED',
								compare: '==',
								value: true,
							},
						],
						resulting_visibility: 'show',
					}
				]
			}
		} ] : [] ),

		{
			option_code: 'NUMBER', // Unique code for the "Number" field.
			input_type: 'int', // Specifies the input type as integer.
			label: escapeHTML( __( 'Throttle duration', 'uncanny-automator' ) ),
			description: escapeHTML( __( 'Set how long the throttle lasts (used with the time unit below).', 'uncanny-automator' ) ),
			placeholder: escapeHTML( __( 'e.g. 30', 'uncanny-automator' ) ),
			min_number: 1, // Minimum value allowed for "Times" (must be 1 or greater).
			supports_tokens: false, // Specifies that this field does not support tokens.
			required: true,
			dynamic_visibility: {
				default_state: 'hidden',
				visibility_rules: [
					{
						operator: 'AND',
						rule_conditions: [
							{
								option_code: 'ENABLED',
								compare: '==',
								value: true,
							},
						],
						resulting_visibility: 'show',
					}
				]
			}
		},
		{
			option_code: 'UNIT', // Unique code for the "Unit" field.
			input_type: 'select', // Specifies the input type as integer.
			label: escapeHTML( __( 'Duration unit', 'uncanny-automator' ) ),
			description: escapeHTML( __( 'Select the unit of time (seconds, minutes, hours, days, or years).', 'uncanny-automator' ) ),
			supports_tokens: false, // Specifies that this field does not support tokens.
			options_show_id: false, // Specifies that the option ID should not be shown in the label.
			required: true,
			options: [
				{
					value: 'seconds',
					text: escapeHTML( __( 'Seconds', 'uncanny-automator' ) ),
				},
				{
					value: 'minutes',
					text: escapeHTML( __( 'Minutes', 'uncanny-automator' ) ),
				},
				{
					value: 'hours',
					text: escapeHTML( __( 'Hours', 'uncanny-automator' ) ),
				},
				{
					value: 'days',
					text: escapeHTML( __( 'Days', 'uncanny-automator' ) ),
				},
				{
					value: 'weeks',
					text: escapeHTML( __( 'Weeks', 'uncanny-automator' ) ),
				},
				{
					value: 'months',
					text: escapeHTML( __( 'Months', 'uncanny-automator' ) ),
				},
				{
					value: 'years',
					text: escapeHTML( __( 'Years', 'uncanny-automator' ) ),
				},
			],
			dynamic_visibility: {
				default_state: 'hidden',
				visibility_rules: [
					{
						operator: 'AND',
						rule_conditions: [
							{
								option_code: 'ENABLED',
								compare: '==',
								value: true,
							},
						],
						resulting_visibility: 'show',
					}
				]
			}
		}
	];

	/**
	 * Renders the label template.
	 * @return {TemplateResult} The label template.
	 */
	get _templateLabel() {
		return html`
			<div class="setting-label">
				${ escapeHTML( __( 'Throttle', 'uncanny-automator' ) ) }: <uo-chip color="info" size="xsmall" filled>${ escapeHTML( __( 'New', 'uncanny-automator' ) ) }</uo-chip>
			</div>
		`;
	}

	/**
	 * Retrieves a readable version of the "Throttle" field value for display.
	 *
	 * @return {Number} The number of throttle
	 */
	get readableValue() {
		const isEnabled     = this.getFieldValue( 'ENABLED' );
		const number        = this.getFieldValue( 'NUMBER' );
		const unit          = this.getFieldValue( 'UNIT' );
		const throttleScope = this.recipeType === 'user' ? this.getFieldValue( 'PER_RECIPE_OR_USER' ) : 'recipe';

		// Check if the setting is not enabled
		if ( isEmpty( isEnabled ) || ! isEnabled ) {
			return escapeHTML( __( 'Unlimited', 'uncanny-automator' ) );
		}

		// If the throttle scope is per recipe, return the formatted duration
		if ( throttleScope === 'recipe' ) {
			return getFormattedDuration( unit, number, true );
		}

		// If the throttle scope is per user, return the formatted duration and per user text
		return sprintf(
			escapeHTML( __( '%1$s, per user', 'uncanny-automator' ) ),
			getFormattedDuration( unit, number, true )
		);
	}

	/**
	 * Opens the helper for the setting.
	 */
	helperCallback() {
		openWindow( {
			url: 'https://automatorplugin.com/knowledge-base/?utm_source=uncanny-automator&utm_medium=in-plugin&utm_content=publish-metabox-recipe-throttle-question-icon',
			w: 450
		} )
	}
}
